/**
 * GateBOXX passkey (WebAuthn) plumbing — native browser API, no library.
 *
 * Device trust can move from the copyable browser cookie to a TPM /
 * Secure-Enclave passkey (owner ask, 2026-07-17). SaaSBOXX serialises WebAuthn
 * options with base64url strings for the byte fields; the browser needs
 * ArrayBuffers, and the ceremony result must return as base64url. These
 * converters are the whole bridge, shared by the admin (staff) and member
 * login pages so the ceremony is written once.
 *
 * Inert unless a tenant sets require_passkey — with the flag off (every tenant
 * today) none of this runs: the login carries no assertion and the response
 * offers no enrol.
 */

// WebAuthn assert options as SaaSBOXX serialises them (base64url strings for
// the byte fields) — converted to ArrayBuffers before navigator.credentials.
export interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: Array<{ id: string; type: string; transports?: string[] }>;
  userVerification?: string;
  [k: string]: unknown;
}

// The step-up a require_passkey=required tenant makes the login resubmit carry:
// the navigator.credentials.get() result (WebAuthn JSON) plus the login host.
export interface PasskeyStepUp {
  assertion: unknown;
  rpId: string;
  origin: string;
}

// Device-trust pairing/passkey data the login response carries only while
// something needs doing (absent in the steady state). passkey_enroll asks the
// FE to run navigator.credentials.create(); the rest drive the refusal screen.
export interface DeviceTrustInfo {
  reason?: string;
  detail?: string;
  mode?: string;
  pairing_code?: string;
  binding_status?: string;
  device_status?: string;
  collect_contact?: boolean;
  contact_required?: boolean;
  passkey_enroll?: boolean;
  passkey_required?: boolean;
  passkey_challenge?: PublicKeyCredentialRequestOptionsJSON | string | null;
}

const b64urlToBuf = (value: string): ArrayBuffer => {
  const pad = '='.repeat((4 - (value.length % 4)) % 4);
  const b64 = (value + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
};

const bufToB64url = (buf: ArrayBuffer): string => {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const passkeySupported = (): boolean =>
  typeof window !== 'undefined' && !!window.PublicKeyCredential
  && !!(navigator.credentials && navigator.credentials.create);

// The challenge arrives JSON-encoded on the wire (the backend json.dumps() it so
// DRF's ErrorDetail can't stringify its numeric leaves, e.g. timeout 60000 ->
// "60000"). Parse it back to real WebAuthn options; tolerate a raw object too.
export const parsePasskeyChallenge = (
  raw: unknown,
): PublicKeyCredentialRequestOptionsJSON | undefined => {
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) as PublicKeyCredentialRequestOptionsJSON; }
    catch { return undefined; }
  }
  if (raw && typeof raw === 'object') {
    return raw as PublicKeyCredentialRequestOptionsJSON;
  }
  return undefined;
};

// Enrol a platform passkey on this already-approved browser:
// register-begin → navigator.credentials.create() → register-finish. The
// identity is this browser's own httpOnly cookie (rides same-origin). Returns
// true only when a credential was stored; every failure path returns false so
// the (already successful) login is never blocked.
export const runPasskeyEnroll = async (email: string): Promise<boolean> => {
  if (!passkeySupported()) return false;
  const rp_id = window.location.hostname;
  const origin = window.location.origin;
  const beginRes = await fetch('/api/v1/auth/device-trust/passkey/register-begin/', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ rp_id, origin, email }),
  });
  const begin = await beginRes.json().catch(() => ({} as Record<string, unknown>));
  const options = (begin as Record<string, unknown>)?.options as Record<string, any> | undefined;
  if (!beginRes.ok || !(begin as Record<string, unknown>)?.ok || !options) return false;
  const publicKey = {
    ...options,
    challenge: b64urlToBuf(options.challenge),
    user: { ...options.user, id: b64urlToBuf(options.user.id) },
    excludeCredentials: (options.excludeCredentials || []).map((c: any) => ({
      ...c, id: b64urlToBuf(c.id),
    })),
  } as PublicKeyCredentialCreationOptions;
  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  if (!cred) return false;
  const att = cred.response as AuthenticatorAttestationResponse;
  const credential = {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64url(att.clientDataJSON),
      attestationObject: bufToB64url(att.attestationObject),
      transports: typeof att.getTransports === 'function' ? att.getTransports() : [],
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  };
  const finRes = await fetch('/api/v1/auth/device-trust/passkey/register-finish/', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ rp_id, origin, credential }),
  });
  const fin = await finRes.json().catch(() => ({} as Record<string, unknown>));
  return !!(finRes.ok && (fin as Record<string, unknown>)?.ok);
};

// Prove this device with its enrolled passkey: run navigator.credentials.get()
// against the challenge that rode the passkey_required refusal, returning the
// assertion (WebAuthn JSON) to resubmit the login with. Null when unsupported
// or the OS prompt was dismissed.
export const runPasskeyGet = async (
  challenge: PublicKeyCredentialRequestOptionsJSON,
): Promise<unknown | null> => {
  if (!passkeySupported()) return null;
  const publicKey = {
    ...challenge,
    challenge: b64urlToBuf(challenge.challenge),
    allowCredentials: (challenge.allowCredentials || []).map((c) => ({
      ...c, id: b64urlToBuf(c.id), type: 'public-key' as const,
    })),
  } as unknown as PublicKeyCredentialRequestOptions;
  const assertion = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
  if (!assertion) return null;
  const asr = assertion.response as AuthenticatorAssertionResponse;
  return {
    id: assertion.id,
    rawId: bufToB64url(assertion.rawId),
    type: assertion.type,
    response: {
      clientDataJSON: bufToB64url(asr.clientDataJSON),
      authenticatorData: bufToB64url(asr.authenticatorData),
      signature: bufToB64url(asr.signature),
      userHandle: asr.userHandle ? bufToB64url(asr.userHandle) : null,
    },
    clientExtensionResults: assertion.getClientExtensionResults ? assertion.getClientExtensionResults() : {},
  };
};
